import { isNullOrUndefined } from 'rewire-common';
import { observable } from 'rewire-core';
import { IToggleMenuItem } from 'rewire-ui';
import { ISortCustom, SortInput, SortInputPriority } from '../DataTypes';
import { RouteManagerBase } from '../main-navigation/RouteManager';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import { capitalize } from '../utility/StringUtils';
import { getSortingItemIcon } from './PrioritySortMenu';
import { objectKeys } from '../utility/Object';

export type TApplyFn = () => void;
export type TTGetSortMenuItemsFn = () => IPrioritySortMenuItem[];
export type PrioritySortMode = 'single' | 'multiple';

export interface IPrioritySortMenuSorts extends ISortCustom {}

export interface IPrioritySortMenuItem {
  name: string;
  title: string;
  visible?: boolean | (() => boolean);
  disabled?: boolean | (() => boolean);
  isCustom?: boolean;
}

interface IPrioritySortMenuState {
  mode: PrioritySortMode;
  sorts: IPrioritySortMenuSorts;
}

export default class PrioritySortMenuModel {
  state: IPrioritySortMenuState;

  constructor(
    protected _apply: TApplyFn,
    public _getSortMenuItems: TTGetSortMenuItemsFn,
    public title = 'Sort By:',
  ) {
    this.state = observable({
      mode: 'multiple',
      sorts: {} as IPrioritySortMenuSorts,
    });
  }

  load(sorts?: IPrioritySortMenuSorts, mode?: PrioritySortMode) {
    this.mode = mode ?? 'multiple';
    this.sorts = sorts ?? {};
  }

  get mode(): PrioritySortMode {
    return this.state.mode;
  }

  set mode(value: PrioritySortMode) {
    this.state.mode = value;
  }

  get isSingleSelectMode(): boolean {
    return this.mode === 'single';
  }

  getSortMenuItems() {
    return this._getSortMenuItems().map((i) =>
      Object.assign({}, i, { active: true, toggleIcon: getSortingItemIcon(this, i.name) }),
    );
  }

  get sorts(): IPrioritySortMenuSorts {
    return this.state.sorts;
  }

  set sorts(value: IPrioritySortMenuSorts) {
    this.state.sorts = value;
  }

  get numOfSorts(): number {
    let count = Object.keys(this.sorts).length;
    if ('custom' in this.sorts) {
      count += this.sorts.custom ? this.sorts.custom.length - 1 : -1;
    }
    return count;
  }

  get hasSorts(): boolean {
    return this.numOfSorts > 0;
  }

  handleItemClick = (item: IToggleMenuItem) => {
    if (this.mode === 'single') {
      this.handleSingleItemClick(item);
    } else {
      // multiple
      this.handleMultipleItemClick(item);
    }
  };

  handleSingleItemClick = (item: IToggleMenuItem) => {
    const sortObj = this.getSortByName(item.name);
    this.sorts = {};
    if (!sortObj) {
      if (this.sortIsCustom(item)) {
        this.sorts.custom = [];
        this.sorts.custom.push({ field: item.name, direction: 'asc', priority: 1 });
      } else {
        this.sorts[item.name as keyof IPrioritySortMenuSorts] = { direction: 'asc', priority: 1 } as any;
      }
    } else {
      let direction = (sortObj as SortInput)?.direction;
      if (!direction) {
        direction = 'asc';
      } else if (direction === 'asc') {
        direction = 'desc';
      } else {
        direction = undefined;
      }
      if (direction) {
        if (this.sortIsCustom(item)) {
          this.sorts.custom = [];
          this.sorts.custom.push({ field: item.name, direction: direction, priority: 1 });
        } else {
          this.sorts[item.name as keyof IPrioritySortMenuSorts] = { direction, priority: 1 } as any;
        }
      }
    }
    this._apply();
  };

  handleMultipleItemClick = (item: IToggleMenuItem) => {
    const sortObj = this.getSortByName(item.name) as SortInput | undefined;
    let sortItemDirection = sortObj?.direction;
    let sortPriority = sortObj?.priority;
    if (!sortItemDirection) {
      sortItemDirection = 'asc';
      sortPriority = this.numOfSorts + 1;
    } else if (sortItemDirection === 'asc') {
      sortItemDirection = 'desc';
    } else {
      sortItemDirection = undefined;
    }

    const customSorts = this.sorts.custom;
    const sortObjIsCustom = this.sortIsCustom(item);
    let customSortIdx = undefined;
    let hasCustomSortIdx = false;
    if (sortObjIsCustom) {
      customSortIdx = customSorts?.findIndex((cs) => cs.field === item.name);
      hasCustomSortIdx = !isNullOrUndefined(customSortIdx) && customSortIdx >= 0;
    }
    if (!sortItemDirection) {
      if (sortObjIsCustom) {
        hasCustomSortIdx && customSorts!.splice(customSortIdx!, 1);
        !customSorts!.length && delete this.sorts.custom;
      } else {
        delete this.sorts[item.name as keyof IPrioritySortMenuSorts];
      }
      this.recalculateSortPriorities();
    } else {
      if (sortObjIsCustom) {
        if (hasCustomSortIdx) {
          customSorts![customSortIdx!] = { field: item.name, direction: sortItemDirection, priority: sortPriority };
        } else {
          if (!customSorts) this.sorts.custom = [];
          this.sorts.custom!.push({ field: item.name, direction: sortItemDirection, priority: sortPriority });
        }
      } else {
        this.sorts[item.name as keyof IPrioritySortMenuSorts] = { direction: sortItemDirection, priority: sortPriority } as any;
      }
    }
    this.sorts = { ...this.sorts };
    this._apply();
  };

  getSortByName(name: string) {
    return (this.sorts[name as keyof IPrioritySortMenuSorts] ?? this.sorts['custom']?.find((s) => s.field === name)) as
      | SortInputPriority
      | undefined;
  }

  sortIsCustom(item: IPrioritySortMenuItem): boolean {
    return item.isCustom || !!udfsSubscriptionService.subscriptionModel.getUdfByName(item.name);
  }

  getSortPriority(sortPriority?: number): number {
    return this.isSingleSelectMode ? 1 : (sortPriority ?? 0);
  }

  recalculateSortPriorities() {
    const sortsOrderedArr = [] as any[];
    for (const key of objectKeys(this.sorts)) {
      if (key === 'custom') {
        const customSorts = this.sorts[key];
        customSorts && sortsOrderedArr.push(...customSorts.map((cs) => ({ key: cs.field, priority: cs.priority })));
      } else {
        sortsOrderedArr.push({ key: key, priority: (this.sorts[key] as any).priority });
      }
    }
    sortsOrderedArr.sort((a, b) => (a.priority < b.priority ? -1 : a.priority > b.priority ? 1 : 0));
    for (let i = 0; i < sortsOrderedArr.length; i++) {
      const orderedArrKey: keyof IPrioritySortMenuSorts = sortsOrderedArr[i].key;
      if (this.sorts[orderedArrKey]) {
        (this.sorts[orderedArrKey] as any).priority = i + 1;
      } else {
        const customSort = this.sorts.custom!.find((s) => s.field === orderedArrKey);
        if (customSort) {
          customSort.priority = i + 1;
        }
      }
    }
  }

  getSortTooltip(): string {
    return this.getSortAsString();
  }

  getSortAsString(maxCount?: number): string {
    const keys = objectKeys(this.sorts);
    if (keys.length === 0) {
      return `${this.title} «none»`;
    }

    const sortByFieldNamesArr = keys.flatMap((key) => {
      if (key === 'custom') {
        const customSorts = this.sorts[key]?.map((cs) => {
          const displayName = udfsSubscriptionService.subscriptionModel.getUdfByName(cs.field)?.displayName ?? cs.field;
          return isNullOrUndefined(displayName) ? '' : `${capitalize(displayName)} ${cs.direction!.toUpperCase()}`;
        });
        return customSorts ?? [];
      }
      return `${capitalize(key)} ${(this.sorts[key] as any).direction.toUpperCase()}`;
    });

    if (!isNullOrUndefined(maxCount) && sortByFieldNamesArr.length > maxCount) {
      sortByFieldNamesArr.splice(maxCount, sortByFieldNamesArr.length - maxCount);
      sortByFieldNamesArr.push('....');
    }

    return `${this.title} ${sortByFieldNamesArr.join(', ')}`;
  }
}

export class PermittedPrioritySortMenuModel extends PrioritySortMenuModel {
  constructor(_apply: TApplyFn, _getSortMenuItems: TTGetSortMenuItemsFn, title?: string) {
    super(_apply, _getSortMenuItems, title);
  }

  getSortPriority(sortPriority: number): number {
    let permittedSortPriority = sortPriority;
    for (const key of objectKeys(this.sorts)) {
      const sort = this.sorts[key];
      if (key === 'custom') {
        if (sort) {
          for (const sortInput of sort as any[]) {
            if (sortInput.priority < sortPriority && !RouteManagerBase.current.permittedSorts.includes(sortInput.field)) {
              permittedSortPriority--;
            }
          }
        }
      } else {
        if ((sort as any).priority < sortPriority && !RouteManagerBase.current.permittedSorts.includes(key)) {
          permittedSortPriority--;
        }
      }
    }
    return permittedSortPriority;
  }
}
